<template>
  <div class="popUpWrap">
    <section class="popup light">
      <div class="topArea">
        <h4>{{ t("account-password") }}</h4>
        <div class="exit" @click.stop="dialogStore.close"></div>
      </div>
      <div class="scrollY">
        <ul class="infoList flex">
          <li v-if="userStore.userV2.hasPassword" class="flex">
            <div class="tit">{{ t('account-current-password') }}</div>
            <input
                v-model="currentPassword"
                type="password"
                :placeholder="t('account-current-password')"
                :class="{ itemError: isCurrentPasswordError }"
                data-test="currentPasswordInput"
            >
            <p v-if="isEmptyCurrentPassword" class="errorMessage">{{ t("error-current-password") }}</p>
            <p v-if="isNotSameCurrentPassword" class="errorMessage">{{ t("error-password-incorrect") }}</p>
          </li>
          <li class="flex">
            <div class="tit">{{ t('password-change-new-password') }}</div>
            <input
                v-model="newPassword"
                type="password"
                :placeholder="t('password-change-new-password')"
                :class="{ itemError: isNewPasswordError }"
                data-test="newPasswordInput"
            >
            <p v-if="isEmptyNewPassword" class="errorMessage">{{ t("error-null-new-password") }}</p>
            <p v-if="isLengthError" class="errorMessage">{{ t("error-invalid-password-size") }}</p>
            <p v-if="isInvalidPassword" class="errorMessage">{{ t("error-invalid-password") }}</p>
          </li>
          <li class="flex">
            <div class="tit">{{ t("password-change-confirm-new-password") }}</div>
            <input
                v-model="confirmPassword"
                type="password"
                :placeholder="t('password-change-confirm-new-password')"
                :class="{ itemError: isConfirmPasswordError }"
                data-test="confirmPasswordInput"
            >
            <p v-if="isEmptyConfirmPassword" class="errorMessage">{{ t("error-null-new-password") }}</p>
            <p v-if="isDiscrepancy" class="errorMessage">{{ t("error-null-confirm-new-password") }}</p>
          </li>
        </ul>
        <div class="btnArea">
          <button class="btn" @click.stop="onClickSave">{{ t("common-save") }}</button>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import {useDialogStore} from "~/store/dialog/dialogStore";
import {useI18n} from "vue-i18n";
import {useToastStore} from "~/store/toast/toastStore";
import {ToastType} from "~/components/Common/Toast/model/ToastType";
import {useUserService} from "~/composables/service/useUserService";
import {ref} from "vue";
// eslint-disable-next-line camelcase
import sha3 from 'js-sha3';
import {useUserStore} from "~/store/user/userStore";

const { t } = useI18n();
const { sha3_256 } = sha3;
const { $i18n } = useNuxtApp();

const dialogStore = useDialogStore();
const toastStore = useToastStore();
const userStore = useUserStore();
const userService = useUserService();

const currentPassword = ref("");
const newPassword = ref("");
const confirmPassword = ref("");

/// Validation flags
const isEmptyCurrentPassword = ref(false);
const isNotSameCurrentPassword = ref(false);

const isEmptyNewPassword = ref(false);
const isLengthError = computed(() => newPassword.value.length < 10 && newPassword.value.length > 0);
const isInvalidPassword = computed(() => !passwordValidation(newPassword.value) && newPassword.value.length > 0);

const isEmptyConfirmPassword = ref(false);
const isDiscrepancy = computed(() => newPassword.value !== confirmPassword.value && confirmPassword.value.length > 0);

// Error class bindings
const isCurrentPasswordError = computed(() => isEmptyCurrentPassword.value || isNotSameCurrentPassword.value);
const isNewPasswordError = computed(() => isEmptyNewPassword.value || isLengthError.value || isInvalidPassword.value);
const isConfirmPasswordError = computed(() => isEmptyConfirmPassword.value || isDiscrepancy.value);

const validateInputs = (): boolean => {
  isEmptyCurrentPassword.value = currentPassword.value === "" && userStore.userV2.hasPassword;
  isEmptyNewPassword.value = newPassword.value === "";
  isEmptyConfirmPassword.value = confirmPassword.value === "";

  return !(
      isEmptyCurrentPassword.value ||
      isEmptyNewPassword.value ||
      isEmptyConfirmPassword.value ||
      isNewPasswordError.value ||
      isConfirmPasswordError.value
  );
};

const onClickSave = () => {
  if (!validateInputs()) return;
  changePasswordAction();
};

const changePasswordAction = () => {
  userService
      .changePassword(
          sha3_256(newPassword.value),
          userStore.userV2.hasPassword ? sha3_256(currentPassword.value) : undefined
      )
      .then(() => {
        userService.setUser();
        toastStore.openToast({
          toastType: ToastType.SUCCESS,
          contents: t("password-saving-success"),
        });
        dialogStore.close();
      })
      .catch((e) => handleError(e));
};

const handleError = (error: any) => {
  if (error.response?.data?.code === 4001) {
    isNotSameCurrentPassword.value = true;
  } else {
    toastStore.openToast({
      toastType: ToastType.FAILED,
      contents: t("password-saving-fail"),
    });
  }
};
</script>